import React, { useState, useEffect, useRef, useCallback } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  ButtonGroup,
  Button,
  Spinner,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
  InputGroup,
  InputGroupText,
  Table,
  Badge,
  Progress
} from "reactstrap"
import Chart from "react-apexcharts"
import {
  getRFQTimelineAnalytics,
  getRFQTimelineAnalyticsDetails,
  exportRFQTimelineAnalyticsDetails,
  formatCurrency,
  truncateText
} from "../../../../helpers/rfq_dashboard_api"
import InfoButton from "./InfoButton"

const TimelineAnalytics = ({ filters }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [period, setPeriod] = useState('monthly')

  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [modalType, setModalType] = useState('')
  const [modalTitle, setModalTitle] = useState('')
  const [modalData, setModalData] = useState(null)
  const [modalLoading, setModalLoading] = useState(false)
  const [modalPage, setModalPage] = useState(1)
  const [modalSearch, setModalSearch] = useState('')
  const [modalPerPage, setModalPerPage] = useState(10)
  const [exportLoading, setExportLoading] = useState(false)

  // Chart refs
  const milestoneChartRef = useRef(null)
  const progressChartRef = useRef(null)

  // Handle card/chart click to open modal
  const handleCardClick = (type, title, status = null) => {
    setModalType(type)
    setModalTitle(title)
    setModalPage(1)
    setModalSearch('')
    setModalPerPage(10) // Reset to default
    setModalOpen(true)
    loadModalData(type, 1, '', status, 10)
  }

  // Safe chart click handler for milestone progress
  const handleMilestoneChartClick = useCallback((config) => {
    try {
      // Get current data to avoid stale closure
      const currentData = data || {}
      const currentMilestoneProgress = currentData.milestone_progress || []

      if (!currentMilestoneProgress || !currentMilestoneProgress[config.dataPointIndex]) {
        console.warn('Invalid chart data point index:', config.dataPointIndex)
        return
      }

      const selectedStatus = currentMilestoneProgress[config.dataPointIndex]?.milestone_status
      if (selectedStatus) {
        // Add a small delay to prevent DOM conflicts
        setTimeout(() => {
          try {
            handleCardClick('milestone_progress', `Milestones - ${selectedStatus}`, selectedStatus)
          } catch (modalError) {
            console.error('Modal opening error:', modalError)
          }
        }, 150)
      }
    } catch (error) {
      console.error('Chart click error:', error)
    }
  }, [data, handleCardClick])



  // Load modal data
  const loadModalData = async (type, page = 1, search = '', status = null, perPage = modalPerPage) => {
    try {
      setModalLoading(true)

      const params = {
        type: type,
        period: period,
        page: page,
        per_page: perPage,
        search: search,
        ...filters  // Add main dashboard filters
      }

      // Add status filter for milestone progress
      if (status && type === 'milestone_progress') {
        params.milestone_status = status
      }

      const response = await getRFQTimelineAnalyticsDetails(params)

      if (response && response.status) {
        setModalData(response.data)
      } else {
        throw new Error(response?.message || "Failed to load modal data")
      }
    } catch (err) {
      console.error("Modal data loading error:", err)
      setModalData(null)
    } finally {
      setModalLoading(false)
    }
  }

  // Handle modal pagination
  const handleModalPageChange = (page) => {
    setModalPage(page)
    // For milestone_progress, we need to pass the status filter from the modal title
    let status = null
    if (modalType === 'milestone_progress' && modalTitle) {
      const statusMatch = modalTitle.match(/Milestones - (.+)/)
      if (statusMatch) {
        status = statusMatch[1]
      }
    }
    loadModalData(modalType, page, modalSearch, status, modalPerPage)
  }

  // Handle modal search with debouncing
  const handleModalSearch = (searchValue) => {
    setModalSearch(searchValue)
    setModalPage(1)

    // Debounce search to avoid too many API calls
    clearTimeout(window.timelineAnalyticsSearchTimeout)
    window.timelineAnalyticsSearchTimeout = setTimeout(() => {
      // For milestone_progress, we need to pass the status filter from the modal title
      let status = null
      if (modalType === 'milestone_progress' && modalTitle) {
        const statusMatch = modalTitle.match(/Milestones - (.+)/)
        if (statusMatch) {
          status = statusMatch[1]
        }
      }
      loadModalData(modalType, 1, searchValue, status, modalPerPage)
    }, 500)
  }

  // Handle modal export
  const handleModalExport = async () => {
    try {
      setExportLoading(true)

      const params = {
        type: modalType,
        period: period,
        search: modalSearch,
        ...filters  // Add main dashboard filters
      }

      // For milestone_progress, add status filter from modal title
      if (modalType === 'milestone_progress' && modalTitle) {
        const statusMatch = modalTitle.match(/Milestones - (.+)/)
        if (statusMatch) {
          params.milestone_status = statusMatch[1]
        }
      }

      await exportRFQTimelineAnalyticsDetails(params)
    } catch (err) {
      console.error("Export error:", err)
      alert("Export failed. Please try again.")
    } finally {
      setExportLoading(false)
    }
  }

  // Load timeline analytics data
  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      console.log("Loading timeline analytics with period:", period, "and filters:", filters)

      const response = await getRFQTimelineAnalytics({
        ...filters,
        period: period
      })

      console.log("Timeline analytics response:", response)

      if (response && response.status) {
        setData(response.data)
        console.log("Timeline analytics loaded successfully:", response.data)
      } else {
        throw new Error(response?.message || "Failed to load timeline analytics")
      }
    } catch (err) {
      console.error("Timeline analytics loading error:", err)
      setError(err.message || "Failed to load timeline analytics")
    } finally {
      setLoading(false)
    }
  }

  // Load data when filters or period change
  useEffect(() => {
    loadData()
  }, [filters, period])

  // Cleanup charts on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      // Cleanup any existing chart instances
      if (window.ApexCharts) {
        try {
          const milestoneChart = window.ApexCharts.getChartByID(`milestone-progress-donut-${period}`)
          if (milestoneChart) {
            milestoneChart.destroy()
          }
          const progressChart = window.ApexCharts.getChartByID(`task-progress-line-${period}`)
          if (progressChart) {
            progressChart.destroy()
          }
        } catch (error) {
          console.warn('Chart cleanup error:', error)
        }
      }
    }
  }, [period])

  // Loading state
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <div className="mt-3">
          <h5>Loading Timeline Analytics...</h5>
          <p className="text-muted">Please wait while we fetch the data</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <Alert color="danger" className="text-center">
        <h4 className="alert-heading">Error Loading Timeline Analytics</h4>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={loadData}>
          <i className="bx bx-refresh me-1"></i>
          Try Again
        </button>
      </Alert>
    )
  }

  if (!data) {
    return (
      <div className="text-center py-5">
        <i className="bx bx-time-five display-4 text-muted"></i>
        <h5 className="mt-3">No Timeline Data Available</h5>
        <p className="text-muted">Please adjust your filters and try again.</p>
      </div>
    )
  }

  const {
    summary_cards = {},
    timeline_trends = [],
    milestone_progress = [],
    work_progress_timeline = []
  } = data || {}







  return (
    <div className="timeline-analytics fade-in">
      <style>{`
        .cursor-pointer {
          cursor: pointer;
        }
        .hover-highlight:hover {
          background-color: #f8f9fa;
          border-color: #007bff;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,123,255,0.15);
        }
        .hover-highlight:hover .text-primary {
          color: #0056b3 !important;
        }
        .hover-highlight:hover .text-success {
          color: #155724 !important;
        }
        .hover-highlight:hover .text-warning {
          color: #856404 !important;
        }
        .hover-highlight:hover .text-info {
          color: #0c5460 !important;
        }
        .metric-card {
          transition: all 0.3s ease;
        }
      `}</style>

      {/* Period Selection */}
      {/*<Row className="mb-4">
        <Col lg={12}>
          <Card className="dashboard-card">
            <CardBody className="py-2">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <i className="bx bx-time-five me-2"></i>
                  Timeline Period
                </h6>
                <ButtonGroup>
                  <Button
                    color={period === 'daily' ? 'primary' : 'light'}
                    size="sm"
                    onClick={() => setPeriod('daily')}
                  >
                    Daily
                  </Button>
                  <Button
                    color={period === 'weekly' ? 'primary' : 'light'}
                    size="sm"
                    onClick={() => setPeriod('weekly')}
                  >
                    Weekly
                  </Button>
                  <Button
                    color={period === 'monthly' ? 'primary' : 'light'}
                    size="sm"
                    onClick={() => setPeriod('monthly')}
                  >
                    Monthly
                  </Button>
                  <Button
                    color={period === 'yearly' ? 'primary' : 'light'}
                    size="sm"
                    onClick={() => setPeriod('yearly')}
                  >
                    Yearly
                  </Button>
                </ButtonGroup>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>*/}

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card
            className="metric-card cursor-pointer hover-highlight"
            onClick={() => handleCardClick('total_rfqs', 'All RFQs in Timeline')}
            title="Click to view all RFQs"
            style={{ position: 'relative' }}
          >
            <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
              <InfoButton
                type="timeline_analytics_total_rfqs"
                title="Total RFQs (Timeline) - Business Logic & Calculation"
                size="sm"
                
              />
            </div>
            <CardBody className="text-center">
              <div className="metric-value text-primary">{summary_cards?.total_rfqs || 0}</div>
              <div className="metric-label">Total RFQs</div>
              <small className="text-primary">
                <i className="bx bx-mouse me-1"></i>Click to view details
              </small>
            </CardBody>
          </Card>
        </Col>
        <Col md={4}>
          <Card
            className="metric-card success cursor-pointer hover-highlight"
            onClick={() => handleCardClick('active_milestones', 'Active Milestones')}
            title="Click to view active milestones"
            style={{ position: 'relative' }}
          >
            <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
              <InfoButton
                type="timeline_analytics_active_milestones"
                title="Active Milestones - Business Logic & Calculation"
                size="sm"
                
              />
            </div>
            <CardBody className="text-center">
              <div className="metric-value text-success">{summary_cards?.active_milestones || 0}</div>
              <div className="metric-label">Active Milestones</div>
              <small className="text-success">
                <i className="bx bx-mouse me-1"></i>Click to view details
              </small>
            </CardBody>
          </Card>
        </Col>
        <Col md={4}>
          <Card
            className="metric-card info cursor-pointer hover-highlight"
            onClick={() => handleCardClick('active_tasks', 'Active Tasks with Progress')}
            title="Click to view active tasks"
            style={{ position: 'relative' }}
          >
            <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
              <InfoButton
                type="timeline_analytics_active_tasks"
                title="Active Tasks - Business Logic & Calculation"
                size="sm"
                
              />
            </div>
            <CardBody className="text-center">
              <div className="metric-value text-info">{summary_cards?.active_tasks || 0}</div>
              <div className="metric-label">Active Tasks</div>
              <small className="text-info">
                <i className="bx bx-mouse me-1"></i>Click to view details
              </small>
            </CardBody>
          </Card>
        </Col>
      </Row>





      {/* Milestone and Task Progress Charts */}
      {!loading && data && (
        <Row className="mb-4">
          <Col lg={6}>
            <Card className="dashboard-card">
              <CardBody>
                <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                  <span>
                    <i className="bx bx-task me-2"></i>
                    Milestone Progress Distribution
                  </span>
                  <InfoButton
                    type="timeline_analytics"
                    title="Milestone Progress Distribution - Calculation Logic"
                    size="sm"
                  />
                </CardTitle>
                {milestone_progress && milestone_progress.length > 0 ? (
                  <div key={`milestone-chart-${period}-${milestone_progress.length}`} id="milestone-progress-chart" ref={milestoneChartRef}>
                    <Chart
                      key={`milestone-chart-instance-${period}-${milestone_progress.length}`}
                      options={{
                        chart: {
                          id: `milestone-progress-donut-${period}-${Date.now()}`,
                          type: 'donut',
                          height: 400,
                          events: {
                            dataPointSelection: function(event, chartContext, config) {
                              handleMilestoneChartClick(config)
                            }
                          }
                        },
                        labels: milestone_progress.map(item => item.milestone_status || 'Unknown'),
                        colors: ['#28a745', '#ffc107', '#dc3545', '#17a2b8'],
                        legend: {
                          position: 'bottom'
                        },
                        plotOptions: {
                          pie: {
                            donut: {
                              size: '70%',
                              labels: {
                                show: true,
                                total: {
                                  show: true,
                                  label: 'Total Milestones',
                                  formatter: function (w) {
                                    try {
                                      return w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                                    } catch (error) {
                                      return 0
                                    }
                                  }
                                }
                              }
                            }
                          }
                        },
                        tooltip: {
                          y: {
                            formatter: function(val) {
                              try {
                                return `${val} milestones - Click to view details`
                              } catch (error) {
                                return `${val} milestones`
                              }
                            }
                          }
                        }
                      }}
                      series={milestone_progress.map(item => parseInt(item.count) || 0)}
                      type="donut"
                      height={400}
                    />
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="bx bx-task display-4 text-muted"></i>
                    <p className="text-muted mt-2">No milestone data available</p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
          <Col lg={6}>
            <Card className="dashboard-card">
              <CardBody>
                <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                  <span>
                    <i className="bx bx-trending-up me-2"></i>
                    Task Progress Timeline
                  </span>
                  <InfoButton
                    type="timeline_analytics"
                    title="Task Progress Timeline - Calculation Logic"
                    size="sm"
                  />
                </CardTitle>
                {work_progress_timeline && work_progress_timeline.length > 0 ? (
                  <div key={`progress-chart-${period}-${work_progress_timeline.length}`} id="task-progress-chart" ref={progressChartRef}>
                    <Chart
                      key={`progress-chart-instance-${period}-${work_progress_timeline.length}`}
                      options={{
                        chart: {
                          id: `task-progress-bar-${period}-${Date.now()}`,
                          type: 'bar',
                          height: 350,
                          toolbar: {
                            show: false
                          },
                          zoom: {
                            enabled: false
                          }
                        },
                        plotOptions: {
                          bar: {
                            horizontal: false,
                            columnWidth: '60%',
                            endingShape: 'rounded',
                            borderRadius: 4,
                            dataLabels: {
                              position: 'top'
                            }
                          }
                        },
                        dataLabels: {
                          enabled: true,
                          formatter: function (val) {
                            return val > 0 ? val : ''
                          },
                          offsetY: -20,
                          style: {
                            fontSize: '12px',
                            colors: ['#304758']
                          }
                        },
                        xaxis: {
                          categories: work_progress_timeline.map(item => {
                            // Format period for monthly/yearly display
                            const period_text = item.period || 'Unknown'
                            return period_text.length > 10 ? period_text.substring(0, 10) + '...' : period_text
                          }),
                          title: {
                            text: `Period (${period.charAt(0).toUpperCase() + period.slice(1)} View)`
                          },
                          labels: {
                            rotate: -45,
                            style: {
                              fontSize: '12px'
                            }
                          }
                        },
                        yaxis: [
                          {
                            title: {
                              text: 'Progress Updates'
                            },
                            labels: {
                              formatter: function(val) {
                                return Math.round(val)
                              }
                            }
                          },
                          {
                            opposite: true,
                            title: {
                              text: 'Average Progress %'
                            },
                            labels: {
                              formatter: function(val) {
                                return val + '%'
                              }
                            }
                          }
                        ],
                        colors: ['#007bff', '#28a745'],
                        legend: {
                          position: 'top',
                          horizontalAlign: 'center'
                        },
                        tooltip: {
                          shared: true,
                          intersect: false,
                          y: [
                            {
                              formatter: function(val) {
                                return val + ' updates'
                              }
                            },
                            {
                              formatter: function(val) {
                                return val + '%'
                              }
                            }
                          ]
                        },
                        grid: {
                          borderColor: '#f1f1f1'
                        }
                      }}
                      series={[
                        {
                          name: 'Progress Updates',
                          type: 'column',
                          data: work_progress_timeline.map(item => parseInt(item.progress_updates) || 0)
                        },
                        {
                          name: 'Avg Progress %',
                          type: 'column',
                          data: work_progress_timeline.map(item => parseFloat(item.avg_progress_percentage) || 0)
                        }
                      ]}
                      type="bar"
                      height={350}
                    />
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="bx bx-trending-up display-4 text-muted"></i>
                    <p className="text-muted mt-2">No task progress data available</p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Timeline Trends Chart */}
      {/*
      {!loading && data && timeline_trends && timeline_trends.length > 0 && (
        <Row className="mb-4">
          <Col lg={12}>
            <Card className="dashboard-card">
              <CardBody>
                <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                  <span>
                    <i className="bx bx-line-chart me-2"></i>
                    RFQ Timeline Trends ({period.charAt(0).toUpperCase() + period.slice(1)} View)
                  </span>
                  <InfoButton
                    type="timeline_analytics_trends"
                    title="Timeline Trends - Period-based RFQ Analysis"
                    size="sm"
                  />
                </CardTitle>
                <div key={`timeline-chart-${period}-${timeline_trends.length}`}>
                  <Chart
                    key={`timeline-chart-instance-${period}-${timeline_trends.length}`}
                    options={{
                      chart: {
                        id: `timeline-trends-${period}-${Date.now()}`,
                        type: 'line',
                        height: 350,
                        zoom: {
                          enabled: true
                        },
                        toolbar: {
                          show: true
                        }
                      },
                      dataLabels: {
                        enabled: false
                      },
                      stroke: {
                        curve: 'smooth',
                        width: 3
                      },
                      xaxis: {
                        categories: timeline_trends.map(item => item.period),
                        title: {
                          text: `Time Period (${period.charAt(0).toUpperCase() + period.slice(1)})`
                        }
                      },
                      yaxis: [
                        {
                          title: {
                            text: 'Number of RFQs'
                          },
                          labels: {
                            formatter: function(val) {
                              return Math.round(val)
                            }
                          }
                        },
                        {
                          opposite: true,
                          title: {
                            text: 'Budget Amount'
                          },
                          labels: {
                            formatter: function(val) {
                              return formatCurrency(val)
                            }
                          }
                        }
                      ],
                      tooltip: {
                        shared: true,
                        intersect: false,
                        y: [
                          {
                            formatter: function(val) {
                              return `${val} RFQs`
                            }
                          },
                          {
                            formatter: function(val) {
                              return `${val} Assigned`
                            }
                          },
                          {
                            formatter: function(val) {
                              return formatCurrency(val)
                            }
                          }
                        ]
                      },
                      legend: {
                        position: 'top'
                      },
                      colors: ['#556ee6', '#34c38f', '#f1b44c']
                    }}
                    series={[
                      {
                        name: 'Total RFQs',
                        type: 'line',
                        data: timeline_trends.map(item => parseInt(item.total_rfqs) || 0)
                      },
                      {
                        name: 'Assigned RFQs',
                        type: 'line',
                        data: timeline_trends.map(item => parseInt(item.assigned_rfqs) || 0)
                      },
                      {
                        name: 'Total Budget',
                        type: 'line',
                        yAxisIndex: 1,
                        data: timeline_trends.map(item => parseFloat(item.total_budget) || 0)
                      }
                    ]}
                    height={350}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
      */}

      {/* Details Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="xl">
        <ModalHeader toggle={() => setModalOpen(false)}>
          <i className="bx bx-detail me-2"></i>
          {modalTitle}
        </ModalHeader>
        <ModalBody>
          {/* Search and Controls - Always visible like Overview tab */}
          <Row className="mb-3">
            <Col md={6}>
              <InputGroup>
                <InputGroupText>
                  <i className="bx bx-search"></i>
                </InputGroupText>
                <Input
                  type="text"
                  placeholder="Search RFQs, projects, tasks..."
                  value={modalSearch}
                  onChange={(e) => handleModalSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={2}>
              <div>
                <label className="form-label">Records per page</label>
                <Input
                  type="select"
                  value={modalPerPage}
                  onChange={(e) => {
                    const newPerPage = parseInt(e.target.value)
                    setModalPerPage(newPerPage)
                    setModalPage(1)
                    // For milestone_progress, we need to pass the status filter from the modal title
                    let status = null
                    if (modalType === 'milestone_progress' && modalTitle) {
                      const statusMatch = modalTitle.match(/Milestones - (.+)/)
                      if (statusMatch) {
                        status = statusMatch[1]
                      }
                    }
                    loadModalData(modalType, 1, modalSearch, status, newPerPage)
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>
              </div>
            </Col>
            <Col md={2}></Col>
            <Col md={2} className="text-end">
              <Button
                color="success"
                onClick={handleModalExport}
                disabled={exportLoading}
                block
              >
                {exportLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <i className="bx bx-download me-2"></i>
                    Export CSV
                  </>
                )}
              </Button>
            </Col>
          </Row>

          {/* Data section with loading state */}
          {modalLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <p className="mt-2">Loading details...</p>
            </div>
          ) : modalData && modalData.records ? (
            <>
              {/* Data Table */}
              <div className="table-responsive">
                <Table className="table-nowrap mb-0">
                  <thead>
                    <tr>
                      {modalType === 'total_rfqs' && (
                        <>
                          <th>RFQ#</th>
                          <th>RFQ Title</th>
                          <th>Project</th>
                          <th>Vendor</th>
                          <th>Status</th>
                          <th>Budget</th>
                          <th>Final Amount</th>
                          <th>Estimated Date</th>
                          <th>Completion Date</th>
                          <th>Created Date</th>
                        </>
                      )}
                      {modalType === 'active_milestones' && (
                        <>
                          <th>RFQ#</th>
                          <th>Milestone Title</th>
                          <th>Status</th>
                          <th>Milestone Amount</th>
                          <th>Project</th>
                          <th>Vendor</th>
                          <th>Created Date</th>
                        </>
                      )}
                      {modalType === 'active_tasks' && (
                        <>
                          <th>RFQ#</th>
                          <th>Milestone</th>
                          <th>Task</th>
                          <th>Progress</th>
                          <th>Status</th>
                          <th>Project</th>
                          <th>Milestone Amount</th>
                          <th>Work Timeline</th>
                          <th>Vendor</th>
                        </>
                      )}
                      {modalType === 'milestone_progress' && (
                        <>
                          <th>RFQ#</th>
                          <th>Milestone Title</th>
                          <th>Milestone Amount</th>
                          <th>Average Progress</th>
                          <th>Project</th>
                          <th>Vendor</th>
                          <th>Estimated Date</th>
                          <th>Completion Date</th>
                          <th>Created Date</th>
                          <th>Status</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {modalData.records.length > 0 ? (
                      modalData.records.map((record, index) => (
                        <tr key={index}>
                          {modalType === 'total_rfqs' && (
                            <>
                              <td><strong>{record.rfq_code}</strong></td>
                              <td>{truncateText(record.rfq_title, 30)}</td>
                              <td><strong>{record.project_name}</strong></td>
                              <td><small>{record.vendor_name}</small></td>
                              <td><Badge color="primary" className="badge-soft">{record.status}</Badge></td>
                              <td><strong>{formatCurrency(record.budget)}</strong></td>
                              <td><strong className="text-success">{formatCurrency(record.total_final_amount)}</strong></td>
                              <td>
                                {record.estimated_completion_date ? (
                                  <small className="text-info">
                                    <i className="bx bx-calendar me-1"></i>
                                    {record.estimated_completion_date}
                                  </small>
                                ) : (
                                  <small className="text-muted">Not Set</small>
                                )}
                              </td>
                              <td>
                                {record.completion_date ? (
                                  <small className="text-success">
                                    <i className="bx bx-check-circle me-1"></i>
                                    {record.completion_date}
                                  </small>
                                ) : (
                                  <small className="text-muted">-</small>
                                )}
                              </td>
                              <td><small className="text-muted">{new Date(record.created_at).toLocaleDateString()}</small></td>
                            </>
                          )}
                          {modalType === 'active_milestones' && (
                            <>
                              <td><strong>{record.rfq_code}</strong></td>
                              <td><strong>{record.title}</strong></td>
                              <td><Badge color="warning" className="badge-soft">{record.milestone_status}</Badge></td>
                              <td><strong className="text-success">{formatCurrency(record.payment_amount)}</strong></td>
                              <td><strong>{record.project_name}</strong></td>
                              <td><small>{record.vendor_name}</small></td>
                              <td><small className="text-muted">{new Date(record.created_at).toLocaleDateString()}</small></td>
                            </>
                          )}
                          {modalType === 'active_tasks' && (
                            <>
                              <td><strong className="text-primary">{record.rfq_code}</strong></td>
                              <td>
                                <div>
                                  {record.milestone_title ? (
                                    <>
                                      <strong className="text-success d-block">{truncateText(record.milestone_title, 25)}</strong>
                                      <Badge color="warning" size="sm" className="badge-soft">{record.milestone_status}</Badge>
                                    </>
                                  ) : (
                                    <small className="text-muted">No Milestone</small>
                                  )}
                                </div>
                              </td>
                              <td><strong>{truncateText(record.task, 40)}</strong></td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="flex-grow-1">
                                    <Progress
                                      value={parseInt(record.work_progress?.replace('%', '') || 0)}
                                      color="info"
                                      className="progress-sm"
                                    />
                                  </div>
                                  <div className="flex-shrink-0 ms-2">
                                    <small>{record.work_progress || '0%'}</small>
                                  </div>
                                </div>
                              </td>
                              <td><Badge color="info" className="badge-soft">{record.status}</Badge></td>
                              <td><strong>{record.project_name}</strong></td>
                              <td>
                                {record.payment_amount ? (
                                  <strong className="text-success">{formatCurrency(record.payment_amount)}</strong>
                                ) : (
                                  <small className="text-muted">N/A</small>
                                )}
                              </td>
                              <td>
                                <div>
                                  {record.work_start && <small className="text-muted d-block">Start: {new Date(record.work_start).toLocaleDateString()}</small>}
                                  {record.work_end && <small className="text-muted d-block">End: {new Date(record.work_end).toLocaleDateString()}</small>}
                                  {record.estimated_completion_date && <small className="text-success d-block">Est: {new Date(record.estimated_completion_date).toLocaleDateString()}</small>}
                                </div>
                              </td>
                              <td><small>{record.vendor_name}</small></td>
                            </>
                          )}
                          {modalType === 'milestone_progress' && (
                            <>
                              <td><strong className="text-primary">{record.rfq_code}</strong></td>
                              <td><strong className="text-primary">{truncateText(record.milestone_title, 30)}</strong></td>
                              <td><strong className="text-success">{formatCurrency(record.payment_amount)}</strong></td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="flex-grow-1">
                                    <Progress
                                      value={record.avg_progress || 0}
                                      color="success"
                                      className="progress-sm"
                                    />
                                  </div>
                                  <div className="flex-shrink-0 ms-2">
                                    <small>{record.avg_progress || 0}%</small>
                                  </div>
                                </div>
                              </td>
                              <td><strong>{record.project_name}</strong></td>
                              <td><small>{record.vendor_name}</small></td>
                              <td>
                                {record.estimated_completion_date ? (
                                  <small className="text-info">
                                    <i className="bx bx-calendar me-1"></i>
                                    {record.estimated_completion_date}
                                  </small>
                                ) : (
                                  <small className="text-muted">Not Set</small>
                                )}
                              </td>
                              <td>
                                {record.completion_date ? (
                                  <small className="text-success">
                                    <i className="bx bx-check-circle me-1"></i>
                                    {record.completion_date}
                                  </small>
                                ) : (
                                  <small className="text-muted">-</small>
                                )}
                              </td>
                              <td><small className="text-muted">{new Date(record.created_at).toLocaleDateString()}</small></td>
                              <td><Badge color="warning" className="badge-soft">{record.milestone_status}</Badge></td>
                            </>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={
                          modalType === 'total_rfqs' ? "10" :
                          modalType === 'milestone_progress' ? "10" :
                          modalType === 'active_milestones' ? "7" :
                          modalType === 'active_tasks' ? "9" :
                          "8"
                        } className="text-center py-4">
                          <i className="bx bx-search display-4 text-muted"></i>
                          <p className="text-muted mt-2">No records found</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {modalData.records && modalData.records.length > 0 && (
                <Row className="mt-3">
                  <Col md={6}>
                    <p className="text-muted">
                      Showing {((modalData.pagination?.current_page || 1) - 1) * (modalData.pagination?.per_page || 10) + 1} to {((modalData.pagination?.current_page || 1) - 1) * (modalData.pagination?.per_page || 10) + modalData.records.length} of {modalData.pagination?.total_records || 0} records
                    </p>
                  </Col>
                  <Col md={6}>
                    <div className="d-flex justify-content-end">
                      <Button
                        color="light"
                        size="sm"
                        disabled={!modalData.pagination?.has_prev_page}
                        onClick={() => handleModalPageChange(modalPage - 1)}
                        className="me-2"
                      >
                        <i className="bx bx-chevron-left"></i>
                      </Button>
                      <span className="align-self-center me-2">
                        Page {modalData.pagination?.current_page || 1} of {modalData.pagination?.total_pages || 1}
                      </span>
                      <Button
                        color="light"
                        size="sm"
                        disabled={!modalData.pagination?.has_next_page}
                        onClick={() => handleModalPageChange(modalPage + 1)}
                      >
                        <i className="bx bx-chevron-right"></i>
                      </Button>
                    </div>
                  </Col>
                </Row>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <i className="bx bx-error display-4 text-muted"></i>
              <p className="text-muted mt-2">Failed to load data</p>
            </div>
          )}
        </ModalBody>
      </Modal>
    </div>
  )
}

export default TimelineAnalytics
